export function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Часовой пояс отображения календаря CRM (должен совпадать с бэкендом для start_at/end_at). */
export const CRM_CALENDAR_TIMEZONE = 'Europe/Moscow';

/** Часы:минуты начала события в часовом поясе CRM. */
export function formatEventClockInCrmTz(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '00:00';
  const s = new Intl.DateTimeFormat('ru-RU', {
    timeZone: CRM_CALENDAR_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
  return s.replace(/\u00a0/g, ' ').trim();
}

function moscowWallYmdParts(d: Date): { y: number; m: number; d: number } | null {
  if (Number.isNaN(d.getTime())) return null;
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: CRM_CALENDAR_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const y = Number(parts.find((p) => p.type === 'year')?.value);
  const m = Number(parts.find((p) => p.type === 'month')?.value);
  const day = Number(parts.find((p) => p.type === 'day')?.value);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(day)) return null;
  return { y, m, d: day };
}

function moscowWallYmdNumberFromInstant(d: Date): number | null {
  const p = moscowWallYmdParts(d);
  if (!p) return null;
  return p.y * 10000 + p.m * 100 + p.d;
}

/**
 * Виджет рабочего стола: не показывать событие, если календарный день в МСК уже строго позже последнего дня интервала
 * (start_at / end_at). На странице /calendar и в API данные не меняются.
 */
export function isWorkplaceCalendarEventPastByMoscowCalendarDay(
  row: { start_at: string; end_at: string | null },
  now: Date = new Date(),
): boolean {
  const today = moscowWallYmdNumberFromInstant(now);
  const start = moscowWallYmdNumberFromInstant(new Date(row.start_at));
  const end = row.end_at ? moscowWallYmdNumberFromInstant(new Date(row.end_at)) : start;
  if (today === null || start === null || end === null) return false;
  const lastDay = Math.max(start, end);
  return today > lastDay;
}

/**
 * Календарный день события в CRM (Москва) как локальный Date(y, m-1, d) для подписей в модалках.
 * Интервалы rangeStart/rangeEnd по-прежнему строятся из ISO-инстанта.
 */
export function crmCalendarWallDate(iso: string): Date {
  const d = new Date(iso);
  const p = moscowWallYmdParts(d);
  if (!p) return new Date();
  return new Date(p.y, p.m - 1, p.d);
}

export function monthRangeIso(d: Date): { from: string; to: string } {
  const y = d.getFullYear();
  const m = d.getMonth();
  const last = new Date(y, m + 1, 0);
  return {
    from: `${y}-${pad2(m + 1)}-01`,
    to: `${y}-${pad2(m + 1)}-${pad2(last.getDate())}`,
  };
}

/**
 * Первая и последняя дата в сетке месяца 6×7 (пн–вс как в Calendar.tsx): хвосты прошлого и следующего месяца.
 * Нужно для API календаря, чтобы в «пустых» ячейках 1–7 числа следующего месяца подтягивались ДР и события.
 */
export function monthGridVisibleRangeIso(anchor: Date): { from: string; to: string } {
  const year = anchor.getFullYear();
  const month = anchor.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const daysInMonth = lastDay.getDate();
  const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;

  const prevMonthLastDay = new Date(year, month, 0).getDate();
  const gridFirst =
    startingDayOfWeek === 0
      ? new Date(year, month, 1)
      : new Date(year, month - 1, prevMonthLastDay - (startingDayOfWeek - 1));

  const remainingDays = 42 - (startingDayOfWeek + daysInMonth);
  const gridLast = new Date(year, month + 1, remainingDays);

  const yf = gridFirst.getFullYear();
  const mf = gridFirst.getMonth() + 1;
  const df = gridFirst.getDate();
  const yt = gridLast.getFullYear();
  const mt = gridLast.getMonth() + 1;
  const dt = gridLast.getDate();

  return {
    from: `${yf}-${pad2(mf)}-${pad2(df)}`,
    to: `${yt}-${pad2(mt)}-${pad2(dt)}`,
  };
}

export function calendarEventsQueryKey(from: string, to: string, extraUserIdsKey: string) {
  return ['calendarEvents', from, to, extraUserIdsKey] as const;
}

export function hasProfilePermission(
  profile: { is_system_admin?: boolean; permissions?: Record<string, { slug: string }[]> } | null | undefined,
  slug: string
): boolean {
  if (profile?.is_system_admin) {
    return true;
  }
  if (!profile?.permissions) {
    return false;
  }
  for (const categoryPermissions of Object.values(profile.permissions)) {
    if (Array.isArray(categoryPermissions) && categoryPermissions.some((p) => p.slug === slug)) {
      return true;
    }
  }
  return false;
}
